import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import Table from '@shared/components/Table';
import { additionalAccessService } from '@modules/additionalSettings/additionalAccess/services/additionalAccess';
import { companiesService } from '@shared/services/companiesService';
import { notifyError, notifySuccess } from '@config/tostifyConfig/tostifyConfig';
import api from '@shared/services/api';
import LoadingButton from '@shared/components/LoadingButton';
import { setAdminBranchId, setEditReportTemplates } from '@store/slices/authSlice';

const DEFAULT_USER_ID = 1; // Static id used to load default profiles

export default function ProfileActivation() {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [profiles, setProfiles] = useState([]);
    const [selectedProfile, setSelectedProfile] = useState(null);
    const [profileDetails, setProfileDetails] = useState(null);
    const [userInfo, setUserInfo] = useState({ userName: '', fullName: '' });

    const [tableLoading, setTableLoading] = useState(false);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);

    const [calendarChecked, setCalendarChecked] = useState(false);
    const [normsChecked, setNormsChecked] = useState(false);
    const [fixChecked, setFixChecked] = useState(false);
    const [editReportTemplatesChecked, setEditReportTemplatesChecked] = useState(false);
    const [isDefault, setIsDefault] = useState(false);

    const [branchesData, setBranchesData] = useState([]);
    const [branchesLoading, setBranchesLoading] = useState(false);
    const [selectedAdministerBranch, setSelectedAdministerBranch] = useState(null);
    const [selectedReportsBranches, setSelectedReportsBranches] = useState([]);

    const profileColumns = [
        {
            key: 'isDefault',
            label: t('additionalSettings.additionalAccess.table.headers.activeProfile'),
            render: (item) => item.isDefault ? t('common.yes') : t('common.no')
        },
        {
            key: 'shortDescription',
            label: t('additionalSettings.additionalAccess.table.headers.titleProfile')
        },
        {
            key: 'longDescription',
            label: t('additionalSettings.additionalAccess.table.headers.descriptionProfile')
        }
    ];

    useEffect(() => {
        fetchBranches();
        fetchProfiles();
    }, []);

    useEffect(() => {
        if (selectedProfile) {
            fetchProfileDetails(selectedProfile.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProfile?.id]);

    const fetchBranches = async () => {
        try {
            setBranchesLoading(true);
            const companies = await companiesService.getAllCompanies();
            setBranchesData(companies || []);
        } catch (error) {
            console.error('Error fetching companies:', error);
            setBranchesData([]);
        } finally {
            setBranchesLoading(false);
        }
    };

    const extractUserInfo = (profile) => {
        if (!profile) return { userName: '', fullName: '' };
        const userName = profile.userCode || profile.userName || profile.username || profile.user || '';
        const fullName = profile.userFullName || profile.fullName || profile.personFullName || profile.name || '';
        return { userName, fullName };
    };

    const fetchProfiles = async () => {
        try {
            setTableLoading(true);
            const response = await additionalAccessService.getHrProfilesUsersByUserId(DEFAULT_USER_ID);
            const mappedProfiles = Array.isArray(response) ? response.map(profile => ({
                id: profile.id,
                shortDescription: profile.shortDescription,
                longDescription: profile.longDescription,
                isDefault: profile.default === true,
                raw: profile
            })) : [];
            setProfiles(mappedProfiles);
            const info = mappedProfiles.length > 0 ? extractUserInfo(mappedProfiles[0].raw) : { userName: '', fullName: '' };
            setUserInfo(info);
        } catch (error) {
            console.error('Error fetching default profiles:', error);
            setProfiles([]);
            setUserInfo({ userName: '', fullName: '' });
            notifyError(t('common.errorMessage'));
        } finally {
            setTableLoading(false);
        }
    };

    const fetchProfileDetails = async (profileId) => {
        if (!profileId) {
            clearDetails();
            return;
        }

        try {
            setDetailsLoading(true);
            const details = await additionalAccessService.getHrProfilesUsers(profileId);
            setProfileDetails(details);
            setCalendarChecked(details?.calendar === true);
            setNormsChecked(details?.norms === true);
            setFixChecked(details?.fix === true);
            setEditReportTemplatesChecked(details?.editReportTemplates === true);
            setIsDefault(details?.default === true);
            setSelectedAdministerBranch(details?.branchIdForAdmins || null);
            setSelectedReportsBranches(Array.isArray(details?.branchIdsForReports) ? details.branchIdsForReports : []);
        } catch (error) {
            console.error('Error fetching profile details:', error);
            notifyError(t('common.errorMessage'));
            clearDetails();
        } finally {
            setDetailsLoading(false);
        }
    };

    const handleRowClick = (index) => {
        if (index < 0 || index >= profiles.length) return;
        const record = profiles[index];
        const newSelection = selectedProfile?.id === record.id ? null : record;
        setSelectedProfile(newSelection);
    };

    const clearDetails = () => {
        setProfileDetails(null);
        setCalendarChecked(false);
        setNormsChecked(false);
        setFixChecked(false);
        setEditReportTemplatesChecked(false);
        setIsDefault(false);
        setSelectedAdministerBranch(null);
        setSelectedReportsBranches([]);
    };

    const clearSelection = () => {
        setSelectedProfile(null);
        clearDetails();
    };

    const handleSave = async () => {
        if (!selectedProfile) {
            notifyError(t('common.selectRecord'));
            return;
        }

        try {
            setSaveLoading(true);
            const response = await api.put(`/hr-profiles-default/${selectedProfile.id}`, null, {
                params: { isDefault: true }
            });

            // Update adminBranchId in Redux if profile has branchIdForAdmins
            if (selectedAdministerBranch) {
                dispatch(setAdminBranchId(selectedAdministerBranch));
            }
            dispatch(setEditReportTemplates(Boolean(response?.data?.editReportTemplates)));

            notifySuccess(t('common.updatedSuccessfully'));
            await fetchProfiles();
            await fetchProfileDetails(selectedProfile.id);
        } catch (error) {
            console.error('Error updating default profile:', error);
            notifyError(t('common.errorMessage'));
        } finally {
            setSaveLoading(false);
        }
    };


    const renderUserInfo = () => {
        if (!userInfo.userName && !userInfo.fullName) return null;
        return (
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px', fontSize: '16px' }}>
                <span>{`UserName: ${userInfo.userName || '-'}`}</span>
                <span>{`FullName: ${userInfo.fullName || '-'}`}</span>
            </div>
        );
    };

    const renderFooterButtons = () => (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '20px' }}>
            <LoadingButton
                type="button"
                className="btn btn-primary"
                loading={saveLoading}
                onClick={handleSave}
                disabled={saveLoading || !selectedProfile}
            >
                {t('additionalSettings.profileActivation.table.headers.selectProfile')}
            </LoadingButton>
            <button
                className="form-btn btn-red"
                onClick={clearSelection}
                disabled={saveLoading || !selectedProfile}
            >
                {t('common.cancel')}
            </button>
        </div>
    );

    const tableTitle = (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <h2 style={{ margin: 0 }}>{t('additionalSettings.profileActivation.title') || 'Profile Activation'}</h2>
            {renderUserInfo()}
        </div>
    );

    return (
        <div className="profile-activation-container w-100">
            <Table
                title={tableTitle}
                columns={profileColumns}
                data={profiles}
                loading={tableLoading}
                showCustomSearch={true}
                showPagination={false}
                showPageSizeSelector={false}
                handleRowClick={handleRowClick}
                selectedData={selectedProfile?.id}
            />

            <div className="profile-activation-details" style={{ marginTop: '16px', paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ opacity: selectedProfile ? 1 : 0.6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '16px', marginTop: '12px' }}>
                        <h4 style={{ margin: 0, fontSize: '16px' }}>{t('additionalSettings.profileActivation.table.headers.rightsFor')}</h4>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '16px' }}>
                            <input type="checkbox" checked={calendarChecked} disabled />
                            <span>{t('additionalSettings.additionalAccess.table.headers.calendarSettings')}</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '16px' }}>
                            <input type="checkbox" checked={normsChecked} disabled />
                            <span>{t('additionalSettings.additionalAccess.table.headers.normativeDocumentsSettings')}</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '16px' }}>
                            <input type="checkbox" checked={fixChecked} disabled />
                            <span>{t('additionalSettings.additionalAccess.table.headers.fixPeriodsForAllCompanies')}</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '16px' }}>
                            <input type="checkbox" checked={editReportTemplatesChecked} disabled />
                            <span>{t('additionalSettings.additionalAccess.table.headers.editReportTemplates')}</span>
                        </label>
                    </div>
                    <div style={{ display: 'flex', gap: '12px', marginTop: '12px', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: '260px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <h4 style={{ margin: 0, fontWeight: 600, fontSize: '16px' }}>{t('additionalSettings.additionalAccess.table.headers.administrate')}</h4>
                            <div style={{ backgroundColor: '#f5f5f5', padding: '8px', borderRadius: '4px', minHeight: '120px', overflowY: 'auto', fontSize: '16px' }}>
                                {branchesLoading ? (
                                    <div style={{ textAlign: 'center' }}>Loading...</div>
                                ) : selectedAdministerBranch ? (
                                    branchesData
                                        .filter(branch => branch.id === selectedAdministerBranch)
                                        .map(branch => (
                                            <div key={branch.id} style={{ padding: '4px 0' }}>{branch.name}</div>
                                        ))
                                ) : (
                                    <div style={{ textAlign: 'center' }}>{t('common.dataNotFound')}</div>
                                )}
                            </div>
                        </div>

                        <div style={{ flex: 1, minWidth: '260px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <h4 style={{ margin: 0, fontWeight: 600, fontSize: '16px' }}>{t('additionalSettings.additionalAccess.table.headers.formReports')}</h4>
                            <div style={{ backgroundColor: '#f5f5f5', padding: '8px', borderRadius: '4px', minHeight: '120px', overflowY: 'auto', fontSize: '16px' }}>
                                {branchesLoading ? (
                                    <div style={{ textAlign: 'center' }}>Loading...</div>
                                ) : selectedReportsBranches.length > 0 ? (
                                    branchesData
                                        .filter(branch => selectedReportsBranches.includes(branch.id))
                                        .map(branch => (
                                            <div key={branch.id} style={{ padding: '4px 0' }}>{branch.name}</div>
                                        ))
                                ) : (
                                    <div style={{ textAlign: 'center' }}>{t('common.dataNotFound')}</div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                {renderFooterButtons()}
            </div>
        </div>
    );
}